import React, { useState } from "react";
import { Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const TONES = [["chaleureux", "Chaleureux & bienveillant"], ["pratique", "Pratique, étape par étape"], ["inspirant", "Inspirant (ancré dans la foi)"], ["institutionnel", "Institutionnel & rassurant"]];
const LENGTHS = [["court", "Court (≈ 350 mots)"], ["moyen", "Moyen (≈ 650 mots)"], ["long", "Long (≈ 1 000 mots)"]];
const SELECT = "h-10 w-full rounded-lg border bg-white px-3 text-sm";

// « Rédiger avec l'IA » : l'agent produit un BROUILLON français (+ couverture générée) ; la publication reste humaine (super admin / manager).
export const ResourceAiDialog = ({ open, onOpenChange, categories, onCreated }) => {
  const [topic, setTopic] = useState("");
  const [notes, setNotes] = useState("");
  const [category, setCategory] = useState(categories[0]?.key || "");
  const [tone, setTone] = useState("chaleureux");
  const [length, setLength] = useState("moyen");
  const [withCover, setWithCover] = useState(true);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    if (topic.trim().length < 5) return toast.error("Décris le sujet en quelques mots.");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/resources/ai-draft", { topic: topic.trim(), notes: notes.trim() || undefined, category, tone, length, with_cover: withCover });
      toast.success("Brouillon rédigé par l'IA — relis, ajuste puis publie.");
      if (data.cover_error) toast.warning("Couverture non générée : tu peux réessayer depuis l'éditeur.");
      onOpenChange(false); setTopic(""); setNotes("");
      onCreated?.(data.article);
    } catch (e) { toast.error(e?.friendlyMessage || e?.response?.data?.detail || "L'IA n'a pas pu rédiger l'article."); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
      <DialogContent className="sm:max-w-xl" data-testid="res-ai-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles size={18} />Rédiger avec l'IA</DialogTitle>
          <DialogDescription>L'IA rédige un brouillon complet en français (titre, chapeau, article structuré, méta-description{withCover ? ", couverture" : ""}). Rien n'est publié : tu relis, corriges, puis publies.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <label className="block space-y-1"><span className="text-xs font-semibold">Sujet de l'article *</span>
            <Textarea value={topic} onChange={(e) => setTopic(e.target.value)} rows={2} maxLength={600} placeholder="Ex. : Comment bien protéger son compte Yalola (mot de passe, appareils, code de vérification)" data-testid="res-ai-topic" /></label>
          <label className="block space-y-1"><span className="text-xs font-semibold">Consignes (optionnel)</span>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} maxLength={800} placeholder="Points à aborder, public visé, éléments à éviter…" data-testid="res-ai-notes" /></label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <label className="block space-y-1"><span className="text-xs font-semibold">Catégorie</span>
              <select value={category} onChange={(e) => setCategory(e.target.value)} className={SELECT} data-testid="res-ai-category">{categories.map((c) => <option key={c.key} value={c.key}>{c.label.fr}</option>)}</select></label>
            <label className="block space-y-1"><span className="text-xs font-semibold">Ton</span>
              <select value={tone} onChange={(e) => setTone(e.target.value)} className={SELECT} data-testid="res-ai-tone">{TONES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></label>
            <label className="block space-y-1"><span className="text-xs font-semibold">Longueur</span>
              <select value={length} onChange={(e) => setLength(e.target.value)} className={SELECT} data-testid="res-ai-length">{LENGTHS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></label>
          </div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={withCover} onChange={(e) => setWithCover(e.target.checked)} data-testid="res-ai-cover" />Générer aussi une image de couverture (illustration aux couleurs Yalola)</label>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy} data-testid="res-ai-cancel">Annuler</Button>
          <Button onClick={run} disabled={busy} data-testid="res-ai-run">{busy ? <><Loader2 size={15} className="animate-spin mr-1" />Rédaction en cours…</> : <><Sparkles size={15} className="mr-1" />Rédiger le brouillon</>}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
